import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Form, ListGroup, Modal, Spinner } from 'react-bootstrap-5';

import { debounce, isEmpty } from 'lodash';
import { addItemToCallList, propertiesByAddress } from '../../helpers/prospect_api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faSearch } from '@fortawesome/pro-solid-svg-icons';
import type { InsightsProperty } from 'bundles/connect/types';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import { setCallListItems } from '../store/actions/results';
import { useDispatch, useSelector } from 'react-redux';
import { selectCallListItems } from '../store/selectors/results';

type Props = {
  callListId: number;
}

export default function AddProperty({ callListId }: Props) {
  const dispatch = useDispatch();
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [insightsProperties, setInsightsProperties] = useState<InsightsProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const callListItems = useSelector(selectCallListItems);

  useEffect(() => {
    if (!searchText) return;
    debouncedSearch(searchText);
  }, [searchText]);

  const handleChange = useCallback(e => setSearchText(e.target.value), []);

  const handleAddToList = (property: InsightsProperty) => {
    addItemToCallList(callListId, property.propertyId).then((res) => {
      const newItems = [...callListItems, res];

      setShowAddPropertyModal(false);
      dispatch(setCallListItems(newItems));
      displayToast('Property added to call list', 'success');
    });
  };

  const debouncedSearch = useCallback(debounce((address) => {
    setLoading(true);
    setInsightsProperties([]);
    propertiesByAddress(address).then(setInsightsProperties).finally(() => setLoading(false));
  }, 300), []);

  return (
    <>
      <Button icon={faPlusCircle} label="Add Property" variant="ghost" onClick={() => setShowAddPropertyModal(true)} />
      <Modal centered show={showAddPropertyModal} onHide={() => setShowAddPropertyModal(false)}>
        <Modal.Header className="flex-row" closeButton>
          <Modal.Title as="h5">Add Property</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2 px-3">
            <Form.Label>Search for a property</Form.Label>

            <div className="with-icon">
              <div className="icon search-icon">
                <FontAwesomeIcon icon={faSearch}/>
              </div>

              <Form.Control autoFocus type="text" onChange={handleChange}/>
            </div>
          </Form.Group>
          {loading && (
            <div className="text-center p-3">
              <Spinner animation="border" variant="secondary" />
            </div>
          )}
          {isEmpty(insightsProperties) && !loading && searchText ? (
            <div className="py-2 px-3">No results found</div>
          ) : (
            <ListGroup style={{ maxHeight: '200px', overflow: 'auto' }}>
              {
                insightsProperties.map(property => (
                  <ListGroup.Item
                    action
                    className="border-0 text-muted fw-bolder d-flex flex-grow-1"
                    key={property.propertyId}
                    onClick={() => handleAddToList(property)}
                  >
                    {property.address}, {property.city}, {property.state}
                  </ListGroup.Item>
                ))
              }
            </ListGroup>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
